// Hook custom per recuperare l'albero delle località (province, comuni, frazioni, quartieri) tramite API.
// Nota: uso axios e TypeScript per tipizzare la risposta e gestire errori e loading.

import { useState, useEffect } from 'react';
import axios from 'axios';

// Prendo la base URL dell'API dalle variabili d'ambiente
const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

// Tipi per rappresentare le varie entità della gerarchia geografica
interface Frazione {
    id: number;
    nome: string;
    slug: string | null;
}

interface Quartiere {
    id: number;
    nome: string | null;
    slug: string | null;
}

interface FullComune {
    codiceIstat: string;
    nome: string | null;
    slug: string | null;
    cap: string | null;
    frazioni: Frazione[];
    quartieri: Quartiere[];
}

export interface FullProvince {
    codiceIstat: string;
    sigla: string;
    nome: string | null;
    slug: string;
    comuni: FullComune[];
}

/**
 * useGetLocationTree
 * @returns { locationTree, loadingLocations, locationError }
 *
 * Nota per il prossimo dev:
 * - `locationTree` contiene la struttura gerarchica province → comuni → frazioni → quartieri
 * - `loadingLocations` è true mentre la chiamata è in corso
 * - `locationError` contiene un messaggio di errore in caso di fallimento
 */
export const useGetLocationTree = () => {
    const [data, setData] = useState<FullProvince[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Funzione auto-invocata async per recuperare i dati
        (async () => {
            try {
                setLoading(true);
                setError(null);

                // Chiamata GET all'endpoint delle località
                const response = await axios.get<FullProvince[]>(`${API_BASE_URL}/locations/tree`);
                setData(response.data);
            } catch (err) {
                setError('Errore durante il recupero della struttura delle località');
                console.error("Errore in useGetLocationTree:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, []); // chiamata solo al mount del componente

    return { locationTree: data, loadingLocations: loading, locationError: error };
};

export default useGetLocationTree;